import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Breadcrumb from "../../../../Components/Utils/Breadcrumb";
import { BackButton } from "../../../../Components/Utils/Buttons";
import DataTable from "../../../../Components/Others/DataTable";
import NuDate from "../../../../Components/Utils/NuDate";
import { GetRole } from "../../../../Components/Utils/roles";
import AccessDenied from "../../../../Components/Utils/AccessDenied";
import axiosInstance from "../../../../Services/axiosCommon";
import Select from "react-select";
import UseDropDown from "../../../../Components/Hooks/useDropDown";
import useDebounce from "../../../../Components/Hooks/useDebounce";
import SearchBar from "../../../../Components/Utils/SearchBar";

const formatFieldName = (key) => {
    if (!key) return "";
    let formatted = key.replace(/([A-Z])/g, " $1");
    formatted = formatted.replace(/[_-]/g, " ");
    return formatted
        .split(" ")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
};

const formatValue = (val) => {
    if (val === null || val === undefined || val === "") return "null";
    if (typeof val === "boolean") return val ? "true" : "false";
    if (typeof val === "object") return JSON.stringify(val);
    return String(val);
};

const ActionLogIndex = () => {
    const { t } = useTranslation();
    const [page, setPage] = useState(1);
    const [pageCount, setPageCount] = useState(
        localStorage.getItem("perPageView") || 20
    );
    const [data, setData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);

    const [search, setSearch] = useState("");
    const [userId, setUserId] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const debouncedSearch = useDebounce(search, 500);

    const { data: userList } = UseDropDown("/user/drop-down");

    const getData = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get("/activity-logs", {
                params: {
                    page: page,
                    per_page: pageCount,
                    description: debouncedSearch,
                    userId: userId,
                    start_date: startDate,
                    end_date: endDate,
                },
            });

            // Handle the API responses:
            // Format 1: res.data.data is the direct array (returned by the server)
            // Format 2: res.data.data.data is the nested array
            const responseData = res?.data;
            if (responseData) {
                if (Array.isArray(responseData.data)) {
                    setData(responseData.data);
                    setTotalCount(responseData.total || responseData.data.length || 0);
                } else if (responseData.data && Array.isArray(responseData.data.data)) {
                    setData(responseData.data.data);
                    setTotalCount(responseData.data.total || responseData.data.data.length || 0);
                } else {
                    setData([]);
                    setTotalCount(0);
                }
            } else {
                setData([]);
                setTotalCount(0);
            }
        } catch (error) {
            console.error("Error fetching activity logs:", error);
            setData([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (GetRole()) {
            getData();
        }
    }, [page, pageCount, debouncedSearch, userId, startDate, endDate]);

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setPage(1);
    };

    const handleUserChange = (option) => {
        setUserId(option ? option.value : "");
        setPage(1);
    };

    const handleStartDateChange = (e) => {
        setStartDate(e.target.value);
        setPage(1);
    };

    const handleEndDateChange = (e) => {
        setEndDate(e.target.value);
        setPage(1);
    };

    const handleClearFilters = () => {
        setSearch("");
        setUserId("");
        setStartDate("");
        setEndDate("");
        setPage(1);
    };

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: "34px",
            height: "34px",
            border: "0.5px solid #EAEBED",
            borderRadius: "0.375rem",
            boxShadow: "none",
            "&:hover": {
                borderColor: "#cbd5e1"
            },
            fontSize: "12px",
            fontWeight: "500"
        }),
        valueContainer: (provided) => ({
            ...provided,
            height: "32px",
            padding: "0 6px"
        }),
        input: (provided) => ({
            ...provided,
            margin: "0px"
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            height: "32px"
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            padding: "4px"
        }),
        clearIndicator: (provided) => ({
            ...provided,
            padding: "4px"
        }),
        menu: (provided) => ({
            ...provided,
            fontSize: "12px",
            zIndex: 9999
        })
    };

    if (!GetRole()) {
        return <AccessDenied />;
    }

    const headers = [
        { label: t("Date"), width: "w-[15%] py-2 pl-8 text-left" },
        { label: t("User"), width: "w-[10%] py-2 text-left px-4" },
        { label: t("Action"), width: "w-[50%] py-2 text-left px-4" },
    ];

    return (
        <div className="w-full h-full px-4 py-2">
            <div className="w-full flex justify-between items-center">
                <Breadcrumb
                    items={[
                        { name: t("Settings") },
                        { name: t("Action Log") },
                    ]}
                />
                <BackButton />
            </div>
            <div className="w-full h-[calc(100vh-145px)] bg-white mt-3 mx-auto p-2 rounded-md">
                <div className="w-full flex flex-col xl:flex-row justify-between items-start xl:items-center gap-3 mb-4 px-3">
                    <p className="text-sm font-semibold text-[#111537] whitespace-nowrap">
                        {t("Logs")} : {totalCount}
                    </p>
                    <div className="flex flex-wrap items-center gap-3 w-full xl:w-auto">
                        <SearchBar
                            value={search}
                            handleChange={handleSearchChange}
                        />

                        <div className="w-48">
                            <Select
                                options={userList}
                                value={userList ? userList.find(opt => opt.value === userId) : null}
                                onChange={handleUserChange}
                                placeholder={t("Select User")}
                                isClearable
                                styles={customSelectStyles}
                            />
                        </div>

                        <div className="flex items-center gap-1.5 border border-[#EAEBED] bg-white rounded-md px-2 h-[34px]">
                            <span className="text-xs text-slate-400 font-medium">{t("From")}:</span>
                            <input
                                type="date"
                                value={startDate}
                                onChange={handleStartDateChange}
                                className="outline-none border-none text-xs text-slate-700 bg-transparent cursor-pointer"
                            />
                        </div>

                        <div className="flex items-center gap-1.5 border border-[#EAEBED] bg-white rounded-md px-2 h-[34px]">
                            <span className="text-xs text-slate-400 font-medium">{t("To")}:</span>
                            <input
                                type="date"
                                value={endDate}
                                onChange={handleEndDateChange}
                                className="outline-none border-none text-xs text-slate-700 bg-transparent cursor-pointer"
                            />
                        </div>

                        {(search || userId || startDate || endDate) && (
                            <button
                                onClick={handleClearFilters}
                                className="h-[34px] px-3 cursor-pointer border border-red-200 text-red-600 hover:bg-red-50 outline-none font-semibold text-xs rounded-md flex justify-center items-center gap-1 transition-all"
                            >
                                {t("Clear")}
                            </button>
                        )}
                    </div>
                </div>
                <DataTable
                    headers={headers}
                    total={totalCount}
                    pageIndex={page}
                    setPageIndex={setPage}
                    dataCount={pageCount}
                    setDataCount={setPageCount}
                    height="h-[calc(100vh-200px)]"
                    bodyHeight="max-h-[calc(100vh-220px)]"
                >
                    {loading ? (
                        <tr>
                            <td colSpan={headers.length} className="text-center py-8 text-slate-400">
                                {t("Loading...")}
                            </td>
                        </tr>
                    ) : data && data.length > 0 ? (
                        data.map((item, index) => {
                            const dateVal = item?.timestamp || item?.created_at || item?.createdAt;
                            const userName = item?.userName || item?.user?.name || item?.causer?.name || item?.user_name || item?.email || item?.userId || "System";
                            const desc = item?.description || item?.event || "N/A";

                            let updateDetails = "";
                            if (
                                item?.action?.toUpperCase() === "UPDATE" &&
                                item?.changes &&
                                typeof item.changes === "object"
                            ) {
                                const changeEntries = Object.entries(item.changes);
                                if (changeEntries.length > 0) {
                                    const formattedChanges = changeEntries.map(([key, val]) => {
                                        const fieldName = formatFieldName(key);
                                        const oldVal = formatValue(val?.old);
                                        const newVal = formatValue(val?.new);
                                        return `${fieldName}: ${oldVal} -> ${newVal}`;
                                    });
                                    updateDetails = ` Updated fields: ${formattedChanges.join(", ")}`;
                                }
                            }

                            return (
                                <tr
                                    className="odd:bg-white even:bg-slate-50 hover:text-baseColor-primary hover:shadow-md hover:scale-y-105"
                                    key={index}
                                >
                                    <td className="py-3 pl-8 pr-2 text-left w-[15%]">
                                        {dateVal ? (
                                            <NuDate value={dateVal} format="DD-MM-YYYY HH:mm:ss" />
                                        ) : (
                                            "-"
                                        )}
                                    </td>
                                    <td className="py-3 px-4 text-left w-[10%] font-medium text-slate-700">
                                        {userName}
                                    </td>
                                    <td className="py-3 px-4 text-left w-[50%]">
                                        <p className="text-slate-600 text-xs md:text-sm leading-relaxed">
                                            {desc}
                                            {updateDetails && (
                                                <span className="text-slate-500 font-medium ml-1.5">
                                                    {updateDetails}
                                                </span>
                                            )}
                                        </p>
                                    </td>
                                </tr>
                            );
                        })
                    ) : (
                        <tr>
                            <td colSpan={headers.length} className="text-center py-8 text-slate-400">
                                {t("No logs found")}
                            </td>
                        </tr>
                    )}
                </DataTable>
            </div>
        </div>
    );
};

export default ActionLogIndex;